/**
 * Comment Mark Extension
 *
 * Adds comment marks to text selections in Tiptap.
 * The mark stores only the commentId - actual comment content is stored in D1 database.
 *
 * How it works:
 * 1. User selects text and clicks "Comment" button in toolbar
 * 2. Frontend creates comment via API, gets commentId
 * 3. setComment(commentId) applies mark to selected text
 * 4. Text is highlighted with yellow background (.comment-highlight CSS class)
 * 5. Clicking highlighted text triggers onCommentClick callback
 * 6. CommentPopover fetches and displays the thread
 * 7. unsetComment(commentId) removes mark when thread is resolved
 *
 * Based on: https://github.com/sereneinserenade/tiptap-comment-extension
 *
 * Requirements implemented:
 * - RICH-08: Inline comments on text selections
 */

import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for configuring the Comment extension
 */
export interface CommentOptions {
  /**
   * HTML attributes to apply to comment spans
   * Default includes 'comment-highlight' class for yellow background
   */
  HTMLAttributes: Record<string, any>;

  /**
   * Callback when user clicks on a comment highlight
   * Receives the commentId and the bounding rect of the clicked element
   * Use rect to position the CommentPopover near the highlighted text
   */
  onCommentClick: (commentId: string, rect: DOMRect) => void;
}

// ============================================================================
// TIPTAP TYPE AUGMENTATION
// ============================================================================

/**
 * Extend Tiptap's Commands interface to add our comment commands.
 * This enables TypeScript autocomplete for editor.commands.setComment()
 */
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      /**
       * Apply comment mark to current selection.
       *
       * @param commentId - The ID of the comment stored in D1
       * @example
       * editor.commands.setComment('abc123');
       */
      setComment: (commentId: string) => ReturnType;

      /**
       * Remove all comment marks with the given commentId.
       * Used when resolving a comment thread.
       *
       * @param commentId - The ID of the comment to remove
       * @example
       * editor.commands.unsetComment('abc123');
       */
      unsetComment: (commentId: string) => ReturnType;
    };
  }
}

// ============================================================================
// EXTENSION
// ============================================================================

/**
 * Comment Mark Extension
 *
 * Creates a mark that highlights text and stores a commentId.
 * The actual comment content lives in D1 database.
 *
 * @example
 * // In your editor configuration:
 * import { Comment } from './extensions/comment';
 *
 * const editor = useEditor({
 *   extensions: [
 *     Comment.configure({
 *       onCommentClick: (commentId, rect) => {
 *         // Show comment popover
 *         setActiveComment({ id: commentId, position: rect });
 *       },
 *     }),
 *   ],
 * });
 */
export const Comment = Mark.create<CommentOptions>({
  // Name of the mark - used in schema and commands
  name: 'comment',

  // -------------------------------------------------------------------------
  // DEFAULT OPTIONS
  // -------------------------------------------------------------------------

  addOptions() {
    return {
      // CSS class for styling - see editor.css for styles
      HTMLAttributes: {
        class: 'comment-highlight',
      },
      // Default no-op click handler
      onCommentClick: () => {},
    };
  },

  // -------------------------------------------------------------------------
  // MARK ATTRIBUTES
  // -------------------------------------------------------------------------

  /**
   * Define the commentId attribute.
   * This is the only data stored on the mark - the rest lives in D1.
   */
  addAttributes() {
    return {
      commentId: {
        // Default to null (no comment)
        default: null,

        // Parse from HTML data attribute
        parseHTML: (element) => element.getAttribute('data-comment-id'),

        // Render as HTML data attribute
        renderHTML: (attributes) => {
          if (!attributes.commentId) {
            return {};
          }
          return {
            'data-comment-id': attributes.commentId,
          };
        },
      },
    };
  },

  // -------------------------------------------------------------------------
  // HTML PARSING
  // -------------------------------------------------------------------------

  /**
   * When loading HTML content, recognize spans with data-comment-id
   * as comment marks.
   */
  parseHTML() {
    return [
      {
        tag: 'span[data-comment-id]',
      },
    ];
  },

  // -------------------------------------------------------------------------
  // HTML RENDERING
  // -------------------------------------------------------------------------

  /**
   * Render comment marks as spans with merged attributes.
   * The '0' placeholder is where child content goes.
   */
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      0,
    ];
  },

  // -------------------------------------------------------------------------
  // COMMANDS
  // -------------------------------------------------------------------------

  addCommands() {
    return {
      /**
       * Apply comment mark to current selection.
       *
       * Usage:
       * 1. User selects text
       * 2. Create comment via API, get ID
       * 3. editor.commands.setComment(commentId)
       */
      setComment:
        (commentId: string) =>
        ({ commands }) => {
          return commands.setMark(this.name, { commentId });
        },

      /**
       * Remove comment marks with given commentId.
       *
       * Usage:
       * 1. User clicks "Resolve" in popover
       * 2. Resolve comment via API
       * 3. editor.commands.unsetComment(commentId)
       *
       * This traverses the entire document finding marks with
       * matching commentId and removes them.
       */
      unsetComment:
        (commentId: string) =>
        ({ tr, state }) => {
          const { doc } = state;
          let found = false;

          // Traverse all nodes in the document
          doc.descendants((node, pos) => {
            // Check each mark on the node
            node.marks.forEach((mark) => {
              if (
                mark.type.name === this.name &&
                mark.attrs.commentId === commentId
              ) {
                // Remove mark from this position range
                tr.removeMark(pos, pos + node.nodeSize, mark.type);
                found = true;
              }
            });
          });

          return found;
        },
    };
  },

  // -------------------------------------------------------------------------
  // PROSEMIRROR PLUGINS
  // -------------------------------------------------------------------------

  /**
   * Add click handler for comment marks.
   *
   * When user clicks on highlighted text, we find the comment mark
   * and call onCommentClick with its ID and bounding rect.
   */
  addProseMirrorPlugins() {
    const { onCommentClick } = this.options;

    return [
      new Plugin({
        key: new PluginKey('commentClickHandler'),

        props: {
          /**
           * Handle click events in the editor.
           *
           * @param view - ProseMirror EditorView
           * @param pos - Document position where click occurred
           * @param event - The DOM click event
           * @returns true if we handled the click, false otherwise
           */
          handleClick: (view, pos, event) => {
            const { state } = view;

            // Resolve position to get marks at that location
            const $pos = state.doc.resolve(pos);
            const marks = $pos.marks();

            // Find comment mark if present
            const commentMark = marks.find(
              (mark) => mark.type.name === 'comment'
            );

            if (commentMark && event.target instanceof HTMLElement) {
              // Get bounding rect for positioning the popover
              const rect = event.target.getBoundingClientRect();

              // Call the callback with commentId and rect
              onCommentClick(commentMark.attrs.commentId, rect);

              // Return true to indicate we handled the click
              return true;
            }

            // Not a comment click - let other handlers process it
            return false;
          },
        },
      }),
    ];
  },
});
